// Prompt 20 — alarm / bildirim API istek ve yanıt şemaları (Zod). Sahibi: API `alarms` + `notifications` modülleri.
import { z } from "zod";
import { permissionSchema, SYSTEM_ROLE_KEYS } from "../auth/permissions.js";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { localDateSchema, moneySchema, timestampSchema, uuidSchema } from "../common/primitives.js";
import { ALARM_RULE_CODES } from "./catalog.js";
import { alarmCategorySchema, alarmEventKindSchema, alarmSeveritySchema, alarmStatusSchema, NOTIFICATION_SKIP_REASONS, notificationChannelSchema, notificationDeliveryStatusSchema, notificationKindSchema, } from "./enums.js";
/**
 * Alarm ve bildirim API'si (Prompt 20, ADR 0020 §13–§14, `docs/domain/alarms.md` §8).
 *
 *  · Alarmlar: bayrak `ALERTS`; okuma `alarms.read` + alarmın KURALININ hedef izni alarmın kapsamında (istasyon alarmı →
 *    o istasyon; şirket alarmı → şirketin tamamı; kiracı alarmı → kiracı), eylemler `alarms.manage` (aynı kapsam),
 *    kural geçersiz kılmaları `alarms.rules.manage` (kiracı düzeyi kiracı kapsamı, istasyon düzeyi o istasyon).
 *  · Bildirimler KİŞİSELDİR: izin istemez, her sorgu oturum kullanıcısıyla süzülür. Yanıtlar e-posta / telefon / push
 *    tokenı TAŞIMAZ; teslim günlüğü adres içermez.
 */
// ── Ortak ─────────────────────────────────────────────────────────────────────
export const alarmRuleCodeSchema = z.enum(ALARM_RULE_CODES);
export const notificationSkipReasonSchema = z.enum(NOTIFICATION_SKIP_REASONS);
/** Eylem notu: kırpılır, 1–1000 karakter (`alarm_events.note` / `alarms.resolution_note` sınırı). */
export const alarmNoteSchema = z
    .string()
    .trim()
    .min(1, "Not boş olamaz")
    .max(1000, "Not en çok 1000 karakter olabilir");
/** "HH:MM" (İstanbul saati). */
export const clockTimeSchema = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Saat "SS:DD" biçiminde olmalıdır (ör. 22:30)')
    .describe('Saat "HH:MM" (İstanbul)');
const queryBoolean = z
    .enum(["true", "false"])
    .optional()
    .transform((value) => value === "true");
/** Virgülle ayrılmış liste (ör. `status=OPEN,ACKNOWLEDGED`); boş → süzme yok. */
const commaList = (item, description) => z
    .string()
    .max(500)
    .optional()
    .describe(description)
    .transform((value) => {
    const items = (value ?? "")
        .split(",")
        .map((part) => part.trim())
        .filter((part) => part.length > 0);
    return items.length > 0 ? items : undefined;
})
    .pipe(z.array(item).max(20).optional());
const versionField = z
    .number()
    .int()
    .positive()
    .optional()
    .describe("İyimser kilit: istemcinin gördüğü sürüm; uyuşmazsa 409 VERSION_CONFLICT");
export const alarmUserRefSchema = z.object({ id: uuidSchema, fullName: z.string() });
export const severityCountsSchema = z.object({
    CRITICAL: z.number().int().nonnegative(),
    IMPORTANT: z.number().int().nonnegative(),
    INFO: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
});
// ── Alarm listesi ────────────────────────────────────────────────────────────
export const alarmListItemSchema = z.object({
    id: uuidSchema,
    ruleCode: alarmRuleCodeSchema,
    ruleLabel: z.string(),
    category: alarmCategorySchema,
    severity: alarmSeveritySchema,
    status: alarmStatusSchema,
    companyId: uuidSchema.nullable(),
    companyName: z.string().nullable(),
    stationId: uuidSchema.nullable().describe("Boş = şirket ya da kiracı düzeyi alarm"),
    stationCode: z.string().nullable(),
    stationName: z.string().nullable(),
    entityType: z.string().nullable(),
    entityId: z.string().nullable(),
    title: z.string(),
    message: z.string(),
    link: z.string().nullable().describe("İlgili ekranın web yolu"),
    occurrenceCount: z.number().int().positive(),
    firstOccurredAt: timestampSchema,
    lastOccurredAt: timestampSchema,
    acknowledgedAt: timestampSchema.nullable(),
    assignee: alarmUserRefSchema.nullable(),
    assignedAt: timestampSchema.nullable(),
    resolvedAt: timestampSchema.nullable(),
    autoResolved: z.boolean(),
    escalationLevel: z.number().int().min(0),
    version: z.number().int(),
});
export const alarmPageSchema = pageOf(alarmListItemSchema);
export const listAlarmsQuerySchema = pageQuerySchema.extend({
    status: commaList(alarmStatusSchema, "Virgülle ayrılmış durumlar (ör. OPEN,ACKNOWLEDGED)"),
    severity: commaList(alarmSeveritySchema, "Virgülle ayrılmış önemler (ör. CRITICAL,IMPORTANT)"),
    stationId: uuidSchema.optional(),
    category: alarmCategorySchema.optional(),
    ruleCode: alarmRuleCodeSchema.optional(),
    openOnly: queryBoolean.describe("true → yalnızca çözülmemiş alarmlar"),
    assignedToMe: queryBoolean.describe("true → bana atanmış (ASSIGNED) alarmlar"),
});
// ── Ana sayfa özeti ──────────────────────────────────────────────────────────
export const alarmSummaryItemSchema = z.object({
    id: uuidSchema,
    ruleCode: alarmRuleCodeSchema,
    severity: alarmSeveritySchema,
    status: alarmStatusSchema,
    title: z.string(),
    stationId: uuidSchema.nullable(),
    stationCode: z.string().nullable(),
    stationName: z.string().nullable(),
    lastOccurredAt: timestampSchema,
    link: z.string().nullable(),
});
export const alarmStationCountsSchema = severityCountsSchema.extend({
    stationId: uuidSchema,
    stationCode: z.string(),
    stationName: z.string(),
});
export const alarmSummarySchema = z.object({
    open: severityCountsSchema.describe("Görünür açık (çözülmemiş) alarmlar"),
    assignedToMe: z.number().int().nonnegative().describe("Bana atanmış açık alarmlar"),
    byStation: z
        .array(alarmStationCountsSchema)
        .describe("İstasyon bazında açık alarmlar (kritik çok olan önce)"),
    withoutStation: severityCountsSchema.describe("Şirket / kiracı düzeyi açık alarmlar"),
    latest: z.array(alarmSummaryItemSchema).describe("En yeni en çok 5 kritik / önemli açık alarm"),
    generatedAt: timestampSchema,
});
// ── Ayrıntı ──────────────────────────────────────────────────────────────────
export const ALARM_AVAILABLE_ACTIONS = ["ACKNOWLEDGE", "ASSIGN", "RESOLVE", "REOPEN", "NOTE"];
export const alarmAvailableActionSchema = z.enum(ALARM_AVAILABLE_ACTIONS);
export const alarmTimelineEntrySchema = z.object({
    id: uuidSchema,
    kind: alarmEventKindSchema,
    actor: z.object({
        id: uuidSchema.nullable().describe("Boş = motor"),
        name: z.string().describe('Kullanıcı adı; motor için "Sistem"'),
    }),
    fromStatus: alarmStatusSchema.nullable(),
    toStatus: alarmStatusSchema.nullable(),
    severity: alarmSeveritySchema.nullable(),
    note: z.string().nullable(),
    assignee: alarmUserRefSchema.nullable().describe("ASSIGNED olayında atanan kişi"),
    data: z.record(z.string(), z.unknown()).describe("Ayrıntı (halka, bildirilen sayısı…); kişisel veri yok"),
    createdAt: timestampSchema,
});
export const alarmDetailSchema = alarmListItemSchema.extend({
    ruleDescription: z.string(),
    rulePermission: permissionSchema.describe("Alarmı görmek için gereken hedef izin"),
    params: z.record(z.string(), z.unknown()).describe("Şablon parametreleri (kişisel veri yok)"),
    acknowledgedBy: alarmUserRefSchema.nullable(),
    assignedBy: alarmUserRefSchema.nullable(),
    resolvedBy: alarmUserRefSchema.nullable(),
    resolutionNote: z.string().nullable(),
    escalatedAt: timestampSchema.nullable(),
    canManage: z.boolean().describe("Oturum kullanıcısı bu alarmda `alarms.manage` taşıyor mu"),
    availableActions: z
        .array(alarmAvailableActionSchema)
        .describe("Durum tablosuna ve yetkiye göre yapılabilecekler"),
    timeline: z.array(alarmTimelineEntrySchema),
});
// ── Eylemler ─────────────────────────────────────────────────────────────────
export const acknowledgeAlarmSchema = z.object({ version: versionField });
export const assignAlarmSchema = z.object({
    userId: uuidSchema.describe("Kiracının etkin üyesi ve alarmı görebilen kullanıcı"),
    note: alarmNoteSchema.optional(),
    version: versionField,
});
export const resolveAlarmSchema = z.object({
    note: alarmNoteSchema.describe("Çözüm notu (zorunlu)"),
    version: versionField,
});
export const reopenAlarmSchema = z.object({
    note: alarmNoteSchema.describe("Yeniden açma gerekçesi (zorunlu)"),
    version: versionField,
});
export const addAlarmNoteSchema = z.object({ note: alarmNoteSchema });
// ── Atanabilecek kişiler ─────────────────────────────────────────────────────
export const assignableUsersQuerySchema = z.object({ alarmId: uuidSchema });
export const assignableUserSchema = z.object({
    userId: uuidSchema,
    fullName: z.string(),
    roles: z.array(z.string()).describe("Rol adları (e-posta / telefon DÖNMEZ)"),
    isCurrentAssignee: z.boolean(),
    isSelf: z.boolean(),
});
export const assignableUserListSchema = z.object({ items: z.array(assignableUserSchema) });
export const ALARM_THRESHOLD_LIMITS = {
    recoveryBufferPercent: {
        min: 0,
        max: 50,
        integer: false,
        money: false,
        label: "Çözülme tamponu (%)",
    },
    warningDays: { min: 1, max: 365, integer: true, money: false, label: "Uyarı süresi (gün)" },
    dropPercent: { min: 5, max: 95, integer: true, money: false, label: "Düşüş eşiği (%)" },
    weeks: { min: 1, max: 12, integer: true, money: false, label: "Karşılaştırılan hafta sayısı" },
    minWeeks: { min: 1, max: 12, integer: true, money: false, label: "En az eşleşen hafta" },
    minBaselineAmount: {
        min: 0,
        max: 10_000_000_000,
        integer: true,
        money: true,
        label: "Asgari taban satış (₺)",
    },
    earliestHour: { min: 0, max: 23, integer: true, money: false, label: "En erken kontrol saati" },
};
export const alarmThresholdLimitSchema = z.object({
    min: z.number().describe("Alt sınır (para eşiğinde kuruş)"),
    max: z.number().describe("Üst sınır (para eşiğinde kuruş)"),
    integer: z.boolean(),
    money: z.boolean().describe("true → değer kuruştur; arayüz ₺ gösterir ve ₺ girer"),
    label: z.string(),
});
const thresholdsSchema = z.record(z.string(), z.number());
export const alarmRuleOverrideSchema = z.object({
    id: uuidSchema,
    enabled: z.boolean().nullable().describe("Boş = üst düzeyden"),
    severity: alarmSeveritySchema.nullable(),
    thresholds: thresholdsSchema.nullable(),
    escalationMinutes: z.number().int().nullable(),
    updatedAt: timestampSchema,
    updatedBy: alarmUserRefSchema.nullable(),
    version: z.number().int(),
});
export const alarmRuleViewSchema = z.object({
    code: alarmRuleCodeSchema,
    category: alarmCategorySchema,
    label: z.string(),
    description: z.string(),
    source: z.enum(["EVENT", "CHECK", "EVENT_AND_CHECK"]),
    permission: permissionSchema.describe("Alarmı görmek için hedef izin"),
    defaultRoles: z.array(z.enum(SYSTEM_ROLE_KEYS)),
    defaults: z.object({
        severity: alarmSeveritySchema,
        severities: z.array(alarmSeveritySchema).describe("Geçersiz kılmada seçilebilecek önemler"),
        thresholds: thresholdsSchema,
        escalationMinutes: z.number().int().nullable().describe("Boş = kural yükseltilmez"),
    }),
    thresholdLimits: z.record(z.string(), alarmThresholdLimitSchema),
    tenantOverride: alarmRuleOverrideSchema.nullable(),
    stationOverride: alarmRuleOverrideSchema.nullable(),
    effective: z.object({
        enabled: z.boolean(),
        forcedSeverity: alarmSeveritySchema.nullable().describe("Boş = sinyalin önemi"),
        thresholds: thresholdsSchema,
        escalationMinutes: z.number().int().nullable(),
    }),
});
export const alarmRulesQuerySchema = z.object({
    stationId: uuidSchema
        .optional()
        .describe("Verilirse istasyon geçersiz kılmaları ve istasyonun etkin değerleri"),
});
export const alarmRuleListSchema = z.object({
    stationId: uuidSchema.nullable(),
    canEditTenant: z.boolean().describe("Kiracı düzeyi geçersiz kılma yazabilir mi (kiracı kapsamlı yetki)"),
    canEditStation: z.boolean().describe("Seçili istasyonun geçersiz kılmasını yazabilir mi"),
    rules: z.array(alarmRuleViewSchema),
});
/** Yol parametresi: kod biçimi burada, katalogda olup olmadığı serviste (bilinmeyen kod 404). */
export const alarmRuleCodeParamsSchema = z.object({
    code: z
        .string()
        .regex(/^[A-Z][A-Z0-9_]{2,59}$/, "Kural kodu büyük harf, rakam ve alt çizgiden oluşur")
        .transform((value) => value),
});
/**
 * Geçersiz kılmayı KAYDET (satır bütün olarak değişir): verilmeyen / boş alan üst düzeyden gelir. Tüm alanlar boşsa satır
 * silinir (varsayılana dönüş). Önem kuralın `severities`'inden, eşik anahtarları kuralın kendi eşiklerinden olmalı;
 * yükseltme süresi yalnızca yükseltilen (`escalationMinutes` tanımlı) kurallarda.
 */
export const saveAlarmRuleOverrideSchema = z.object({
    stationId: uuidSchema.nullable().optional().describe("Boş = kiracı düzeyi"),
    enabled: z.boolean().nullable().optional(),
    severity: alarmSeveritySchema.nullable().optional(),
    thresholds: z
        .record(z.string().regex(/^[a-z][A-Za-z0-9]{0,39}$/), z.number())
        .nullable()
        .optional(),
    escalationMinutes: z.number().int().min(1).max(1440).nullable().optional(),
    version: versionField,
});
export const deleteAlarmRuleOverrideQuerySchema = z.object({
    stationId: uuidSchema.optional().describe("Boş = kiracı düzeyi geçersiz kılma"),
});
// ── Bildirim kutusu ──────────────────────────────────────────────────────────
export const listNotificationsQuerySchema = pageQuerySchema.extend({
    unreadOnly: queryBoolean.describe("true → yalnızca okunmamışlar"),
});
export const notificationUnreadCountSchema = z.object({
    unread: z.number().int().nonnegative(),
    unreadCritical: z.number().int().nonnegative(),
});
export const markAllNotificationsReadSchema = z.object({
    before: timestampSchema
        .optional()
        .describe("Yalnızca bu andan önce oluşanlar (liste yüklendiği an); boş = tümü"),
});
export const markAllNotificationsReadResultSchema = z.object({
    updated: z.number().int().nonnegative(),
    unread: z.number().int().nonnegative(),
});
// ── Tercihler ────────────────────────────────────────────────────────────────
export const notificationPreferenceRowSchema = z.object({
    stationId: uuidSchema.nullable().describe("Boş = tüm istasyonlar"),
    category: alarmCategorySchema.nullable().describe("Boş = tüm kategoriler (kural verilmişse boş olmalı)"),
    ruleCode: alarmRuleCodeSchema.nullable().describe("Boş = tüm kurallar (kategori verilmişse boş olmalı)"),
    channel: notificationChannelSchema,
    enabled: z.boolean(),
    minSeverity: alarmSeveritySchema.nullable().describe("Boş = kanal varsayılanı"),
});
export const notificationChannelDefaultSchema = z.object({
    channel: notificationChannelSchema,
    enabled: z.boolean(),
    minSeverity: alarmSeveritySchema,
});
export const notificationPreferencesSchema = z.object({
    rows: z.array(notificationPreferenceRowSchema),
    roles: z.array(z.enum(SYSTEM_ROLE_KEYS)).describe("Kullanıcının kiracıdaki sistem rolleri"),
    roleDefaults: z.array(notificationChannelDefaultSchema).describe("Satır yokken geçerli rol varsayılanları"),
    channelMinSeverity: z.array(notificationChannelDefaultSchema.omit({ enabled: true })),
    channels: z.array(z.object({ code: notificationChannelSchema, label: z.string() })),
    stations: z.array(z.object({ id: uuidSchema, code: z.string(), name: z.string() })),
    categories: z.array(z.object({ code: alarmCategorySchema, label: z.string() })),
    rules: z.array(z.object({
        code: alarmRuleCodeSchema,
        label: z.string(),
        category: alarmCategorySchema,
        severities: z.array(alarmSeveritySchema),
    })),
});
export const saveNotificationPreferenceRowSchema = z.object({
    stationId: uuidSchema.nullable().default(null),
    category: alarmCategorySchema.nullable().default(null),
    ruleCode: z
        .string()
        .regex(/^[A-Z][A-Z0-9_]{2,59}$/)
        .nullable()
        .default(null)
        .describe("Katalogdaki kural kodu"),
    channel: notificationChannelSchema,
    enabled: z.boolean(),
    minSeverity: alarmSeveritySchema.nullable().default(null),
});
/** Satır kümesi BÜTÜN olarak değişir (listede olmayan satır silinir = rol varsayılanına dönüş). */
export const saveNotificationPreferencesSchema = z.object({
    rows: z.array(saveNotificationPreferenceRowSchema).max(500),
});
// ── Ayarlar ──────────────────────────────────────────────────────────────────
export const notificationSettingsSchema = z.object({
    quietHoursStart: clockTimeSchema.nullable(),
    quietHoursEnd: clockTimeSchema.nullable(),
    muteCriticalInQuietHours: z.boolean(),
    dailyDigestEnabled: z.boolean(),
    dailyDigestTime: clockTimeSchema,
    weeklyDigestEnabled: z.boolean(),
    hasPhone: z.boolean().describe("SMS için profilde telefon var mı (numara DÖNMEZ)"),
    isDefault: z.boolean().describe("Kayıt yok; varsayılanlar gösteriliyor"),
    version: z.number().int().describe("Kayıt yoksa 0"),
    updatedAt: timestampSchema.nullable(),
});
export const saveNotificationSettingsSchema = z
    .object({
    quietHoursStart: clockTimeSchema.nullable(),
    quietHoursEnd: clockTimeSchema.nullable(),
    muteCriticalInQuietHours: z.boolean(),
    dailyDigestEnabled: z.boolean(),
    dailyDigestTime: clockTimeSchema,
    weeklyDigestEnabled: z.boolean(),
    version: z
        .number()
        .int()
        .min(0)
        .optional()
        .describe("İyimser kilit: GET'teki sürüm (kayıt yoksa 0); uyuşmazsa 409 VERSION_CONFLICT"),
})
    .superRefine((value, context) => {
    if ((value.quietHoursStart === null) !== (value.quietHoursEnd === null))
        context.addIssue({
            code: "custom",
            path: ["quietHoursEnd"],
            message: "Sessiz saat başlangıcı ve bitişi birlikte verilmeli ya da ikisi de boş bırakılmalıdır",
        });
    else if (value.quietHoursStart !== null && value.quietHoursStart === value.quietHoursEnd)
        context.addIssue({
            code: "custom",
            path: ["quietHoursEnd"],
            message: "Sessiz saat başlangıcı ve bitişi aynı olamaz",
        });
});
// ── Teslim günlüğü ("bildirim neden gelmedi") ────────────────────────────────
export const notificationDeliveriesQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const notificationDeliverySchema = z.object({
    id: uuidSchema,
    notificationId: uuidSchema,
    notificationKind: notificationKindSchema,
    notificationTitle: z.string(),
    severity: alarmSeveritySchema.nullable(),
    channel: notificationChannelSchema,
    status: notificationDeliveryStatusSchema,
    skipReason: notificationSkipReasonSchema.nullable(),
    skipReasonLabel: z.string().nullable(),
    attempts: z.number().int().nonnegative(),
    lastErrorCode: z.string().nullable(),
    nextAttemptAt: timestampSchema.nullable(),
    sentAt: timestampSchema.nullable(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
});
export const notificationDeliveryListSchema = z.object({ items: z.array(notificationDeliverySchema) });
// ── Test bildirimi ───────────────────────────────────────────────────────────
/**
 * Kullanıcı başına test bildirimi sınırı (dakikada en çok 3; aşılırsa `429 RATE_LIMITED` — `Retry-After` başlığı ve
 * `meta.retryAfterSeconds` aynı saniyeyi taşır).
 */
export const TEST_NOTIFICATION_LIMIT = { max: 3, windowSeconds: 60 };
/**
 * Kendime test bildirimi: tercih / sessiz saat UYGULANMAZ (kullanıcı açıkça istedi); adresi olmayan kanal
 * `SKIPPED(NO_ADDRESS)`. E-posta / SMS / push teslimi `PENDING` yazılır ve worker'ın teslim taramasıyla gönderilir.
 */
export const sendTestNotificationSchema = z.object({
    channels: z
        .array(notificationChannelSchema)
        .max(8)
        .optional()
        .describe("Kanallar; boş ya da verilmezse uygulama içi + adresinizin olduğu tüm kanallar (e-posta / SMS / push)"),
});
export const testNotificationDeliverySchema = z.object({
    deliveryId: uuidSchema.nullable().describe("Teslim kaydı; bildirim yazılmadıysa boş"),
    channel: notificationChannelSchema,
    status: notificationDeliveryStatusSchema.describe("Uygulama içi hemen SENT; diğerleri PENDING (worker gönderir) ya da SKIPPED"),
    skipReason: notificationSkipReasonSchema.nullable(),
    skipReasonLabel: z.string().nullable(),
});
export const testNotificationResultSchema = z.object({
    notificationId: uuidSchema.nullable().describe("Hiçbir kanal gönderilemiyorsa bildirim yazılmaz → boş"),
    inApp: z.boolean().describe("Bildirim kutusunda görünür mü"),
    deliveries: z.array(testNotificationDeliverySchema),
    createdAt: timestampSchema,
});
// ── Özet önizlemesi ──────────────────────────────────────────────────────────
export const DIGEST_KINDS = ["DAILY", "WEEKLY"];
export const digestKindSchema = z.enum(DIGEST_KINDS);
export const digestPreviewQuerySchema = z.object({
    kind: digestKindSchema.default("DAILY").describe("DAILY = sabah özeti, WEEKLY = haftalık özet"),
    day: localDateSchema
        .optional()
        .describe("Sabah özetinde özetin günü (ciro = önceki gün); haftalıkta bu günün haftasının pazartesisi. Boş = bugün (İstanbul)"),
});
const percentChangeSchema = z
    .string()
    .nullable()
    .describe('Yüzde değişim (işaretli, 1 ondalık; ör. "-12.5"); karşılaştırılan dönem 0 ise boş');
export const digestRevenueRowSchema = z.object({
    businessUnit: z
        .string()
        .describe("İş birimi (FUEL, LPG, MARKET, WASH, EV, CAFE, RENTAL, OTHER); toplam satırında TOTAL"),
    label: z.string(),
    amount: moneySchema.describe("Dönemin cirosu (KDV dahil, kuruş)"),
    previous: moneySchema.describe("Önceki gün / önceki hafta"),
    compare: moneySchema.describe("Sabah özetinde geçen haftanın aynı günü; haftalıkta kullanılmaz (0)"),
    changeVsPrevious: percentChangeSchema,
    changeVsCompare: percentChangeSchema,
});
export const digestRevenueSchema = z.object({
    rows: z.array(digestRevenueRowSchema).describe("İş birimi bazında (yalnızca tutarı olanlar)"),
    total: digestRevenueRowSchema,
});
export const digestAlarmItemSchema = z.object({
    alarmId: uuidSchema,
    ruleCode: alarmRuleCodeSchema,
    severity: alarmSeveritySchema,
    title: z.string(),
    stationId: uuidSchema.nullable(),
    firstOccurredAt: timestampSchema,
    link: z.string().describe("Alarm ayrıntısının web yolu (/alarmlar/{id})"),
});
const countAmountSchema = z.object({ count: z.number().int().nonnegative(), amount: moneySchema });
export const digestDeliverySchema = z.object({
    enabled: z.boolean().describe("Ayarlarda bu özet açık mı"),
    time: clockTimeSchema.describe("Gönderim saati (İstanbul; haftalık özet pazartesi aynı saatte)"),
    channels: z.array(notificationChannelSchema).describe("Tercihlere göre kullanılacak kanallar"),
});
export const digestTankItemSchema = z.object({
    tankId: uuidSchema,
    stationId: uuidSchema,
    label: z.string().describe('"IST01 · Tank 2 (Motorin)"'),
    volumeText: z.string().describe('Son okumadaki hacim, Türkçe biçimli litre (ör. "12.345")'),
    level: z.enum(["CRITICAL", "LOW"]),
});
/**
 * Sabah özetinin YAPISAL İÇERİĞİ — tek kaynak: motorun özet kurucusu bu biçimi üretir, özet bildiriminin `data.digest`'i ve
 * önizleme (`dailyDigestPreviewSchema` = içerik + başlık / gövde / gönderim) aynı şemadır.
 */
export const dailyDigestContentSchema = z.object({
    kind: z.literal("DAILY"),
    day: localDateSchema.describe("Özetin günü"),
    revenueDay: localDateSchema.describe("Ciro günü (özetin gününden önceki gün)"),
    revenue: digestRevenueSchema
        .nullable()
        .describe("Önceki güne ve geçen haftanın aynı gününe göre; izin / veri yoksa boş"),
    alarms: z
        .object({
        counts: severityCountsSchema,
        top: z.array(digestAlarmItemSchema).describe("En fazla 5 kritik / önemli"),
    })
        .nullable()
        .describe("Görebildiğim açık alarmlar; yoksa boş"),
    cheques: z
        .object({ collections: countAmountSchema, payments: countAmountSchema })
        .nullable()
        .describe("Bugün vadesi gelen alınan (tahsil) / verilen (ödeme) çek ve senetler"),
    payments: countAmountSchema.nullable().describe("Bugünkü ödeme listeleri (taslak / onayda / onaylı)"),
    tanks: z.array(digestTankItemSchema).nullable().describe("Kritik / düşük tanklar (son 24 saatin okuması)"),
});
/** Haftalık özetin YAPISAL İÇERİĞİ (bildirimin `data.digest`'i ve önizleme aynı şema). */
export const weeklyDigestContentSchema = z.object({
    kind: z.literal("WEEKLY"),
    weekStart: localDateSchema.describe("Özetin gönderildiği pazartesi"),
    from: localDateSchema.describe("Özetlenen hafta: önceki pazartesi"),
    to: localDateSchema.describe("Özetlenen hafta: önceki pazar"),
    revenue: digestRevenueSchema.nullable().describe("Önceki haftaya göre; izin / veri yoksa boş"),
    alarmsByRule: z.array(z.object({
        ruleCode: alarmRuleCodeSchema,
        label: z.string(),
        opened: z.number().int().nonnegative(),
        resolved: z.number().int().nonnegative(),
        stillOpen: z.number().int().nonnegative(),
    })),
    longestOpen: z
        .array(digestAlarmItemSchema.extend({ openDays: z.number().int().nonnegative() }))
        .describe("En uzun açık kalanlar (en fazla 5)"),
});
/** Özet içeriği (`kind` ayırt eder): özet bildiriminin `data.digest`'i. */
export const digestContentSchema = z.discriminatedUnion("kind", [
    dailyDigestContentSchema,
    weeklyDigestContentSchema,
]);
/** Önizlemenin içeriğe eklediği alanlar: bildirimin başlığı / düz metin gövdesi, boşluk ve gönderim ayarı. */
const digestPreviewEnvelope = {
    title: z.string(),
    body: z.string().describe("Gönderilecek düz metin"),
    empty: z.boolean().describe("İçerik yok → özet gönderilmez"),
    delivery: digestDeliverySchema,
};
export const dailyDigestPreviewSchema = dailyDigestContentSchema.extend(digestPreviewEnvelope);
export const weeklyDigestPreviewSchema = weeklyDigestContentSchema.extend(digestPreviewEnvelope);
/** Özet önizlemesi (gönderim YOK, `digest_runs` yazılmaz): oturum kullanıcısının göreceği içerik. */
export const digestPreviewSchema = z.discriminatedUnion("kind", [
    dailyDigestPreviewSchema,
    weeklyDigestPreviewSchema,
]);
// ── Bildirim ─────────────────────────────────────────────────────────────────
/** Bildirimin alarm bağı (`ALARM` / `ALARM_GROUP` / atama): kural ve önem (başlık / gövde bildirimin kendisinde). */
export const notificationAlarmRefSchema = z.object({
    alarmId: uuidSchema,
    ruleCode: alarmRuleCodeSchema,
    severity: alarmSeveritySchema,
});
/** Alarm bildiriminin nedeni: yeni alarm, önemi yükseldi, yükseltildi, size atandı. */
export const NOTIFICATION_REASONS = ["RAISED", "SEVERITY_RAISED", "ESCALATED", "ASSIGNED"];
export const notificationReasonSchema = z.enum(NOTIFICATION_REASONS);
/**
 * Bildirimin YAPISAL verisi (türe göre; kişisel veri YOK). Özet bildiriminde `digest` önizlemeyle AYNI içerik şemasıdır
 * (`digestContentSchema`); alarm bildiriminde `alarms` + `reasons`; test bildiriminde `test` + `channels`. API satırı bu
 * şemayla ayrıştırır: uymayan parça (ör. eski biçimli özet) yanıttan DÜŞER, bildirim düz metin gövdesiyle gösterilir.
 */
export const notificationDataSchema = z.object({
    digest: digestContentSchema
        .optional()
        .describe("DIGEST_DAILY / DIGEST_WEEKLY: özetin yapısal içeriği (önizlemeyle aynı biçim)"),
    alarms: z
        .array(notificationAlarmRefSchema)
        .optional()
        .describe("ALARM / ALARM_GROUP: bildirimdeki alarmlar (kural + önem)"),
    reasons: z.array(notificationReasonSchema).optional().describe("Alarm bildiriminin nedenleri"),
    test: z.boolean().optional().describe("TEST: test bildirimi"),
    channels: z.array(notificationChannelSchema).optional().describe("TEST: istenen kanallar"),
});
export const notificationSchema = z.object({
    id: uuidSchema,
    kind: notificationKindSchema,
    severity: alarmSeveritySchema.nullable(),
    title: z.string(),
    body: z.string(),
    link: z.string().nullable(),
    stationId: uuidSchema.nullable(),
    stationCode: z.string().nullable(),
    category: alarmCategorySchema.nullable(),
    alarmIds: z.array(uuidSchema),
    data: notificationDataSchema,
    readAt: timestampSchema.nullable(),
    createdAt: timestampSchema,
});
export const notificationPageSchema = pageOf(notificationSchema);
//# sourceMappingURL=api.js.map